import { useState } from "react"
import { useIntl } from "react-intl"

import classnames from "classnames"

import { Button, Dropdown, ICONS, StaffBodyTextL, StaffBodyTextS, StaffLabelTextS } from "@doktor-se/bones-ui"
import { OptionProps } from "@doktor-se/bones-ui/dist/components/Dropdown/Dropdown"
import { Clinic, Queue } from "@doktor-se/bones-ui/dist/web-shared/types"

import { useAppSelector } from "lib/hooks"
import { formatDistance } from "lib/utils"
import { clinicSelectors } from "reducers/clinics"
import { queueSelectors } from "reducers/queue/queue.reducer"

import { ConfirmParams } from "../PhysicalToDigitalDialog"

import styles from "./ToPhysicalQueue.module.scss"

const queuesPriority: Record<string, number> = {
  contact_my_clinic: 0,
  contact_my_clinic_admin: 1
}

interface ToPhysicalQueueProps {
  selectedQueue: Queue | undefined
  setSelectedQueue: (queue: Queue | undefined) => void
  onSend: (params: ConfirmParams) => void
}

const ToPhysicalQueue = ({ selectedQueue, setSelectedQueue, onSend }: ToPhysicalQueueProps) => {
  const intl = useIntl()
  const appLanguage = useAppSelector(state => state.app.language)
  const queues = useAppSelector(queueSelectors.selectAll)
  const patientClinics = useAppSelector(state => state.clinics.patientClinics)
  const clinics = useAppSelector(clinicSelectors.selectAll)

  const [selectedClinic, setSelectedClinic] = useState<Clinic | undefined>()
  const [availableClinicQueues, setAvailableClinicQueues] = useState<OptionProps[]>()

  const selectPatientClinic = (clinicId: string) => {
    const selected = clinics.find(c => c.id === clinicId)
    setSelectedClinic(selected)
    const clinicQueues = queues.filter(q => selected?.supported_queue_ids?.includes(q.id))
    if (clinicQueues.length === 1) {
      setSelectedQueue(clinicQueues[0])
    } else {
      setSelectedQueue(undefined)
    }
    const orderedQueues = clinicQueues.sort((a, b) => {
      const prioFirst = queuesPriority[a.queueKey] ?? Infinity
      const prioSecond = queuesPriority[b.queueKey] ?? Infinity
      return prioFirst - prioSecond
    })
    setAvailableClinicQueues(orderedQueues.map(c => ({ id: c.id, name: c.translations[appLanguage] })))
  }

  const selectClinicQueue = (queueId: string) => {
    const selected = queues?.find(q => q.id === queueId)
    if (selected) {
      setSelectedQueue(selected)
    }
  }

  const sendToQueue = () => {
    onSend({ clinicId: selectedClinic?.id })
  }

  return (
    <div className={styles.clinicsList}>
      {patientClinics?.map((c, index) => (
        <div
          key={c.id}
          role="button"
          tabIndex={index}
          onKeyDown={event => {
            if (event.key === "Enter") {
              selectPatientClinic(c.id)
            }
          }}
          onClick={() => selectPatientClinic(c.id)}
          className={classnames(styles.clinicCard, {
            [styles.clinicCardSelected]: selectedClinic?.id === c.id
          })}>
          <div className={styles.clinicDetailsWrapper}>
            <ICONS.ClinicMedium width={38} height={38} fill="var(--color-primary-50)" />
            <div className={styles.clinicDetails}>
              <StaffBodyTextL>{c.name}</StaffBodyTextL>
              <StaffBodyTextS>{c.address}</StaffBodyTextS>
              {c.distance !== null && (
                <div className={styles.clinicDistance}>
                  {c.listedAt ? (
                    <StaffLabelTextS style={{ marginTop: "8px" }}>
                      {intl.formatMessage({ id: "label.to_queue.listed_here" })}
                    </StaffLabelTextS>
                  ) : (
                    <StaffBodyTextS>{`${formatDistance(c.distance)} ${intl.formatMessage({ id: "label.to_queue.distance_suffix" })}`}</StaffBodyTextS>
                  )}
                  <ICONS.ChevronRight width={14} height={14} />
                </div>
              )}
            </div>
          </div>

          {selectedClinic?.id === c.id && (
            <div className={styles.clinicQueueWrapper}>
              {availableClinicQueues && !!availableClinicQueues.length && (
                <div>
                  <StaffBodyTextS style={{ marginBottom: "8px" }}>
                    {intl.formatMessage({ id: "label.queues" })}
                  </StaffBodyTextS>
                  <Dropdown
                    label="Queue"
                    onSelectionChange={selectClinicQueue}
                    defaultSelectedKey={selectedQueue?.id}
                    placeholder={intl.formatMessage({ id: "label.to_queue.clinic.select" })}
                    fullWidth
                    items={availableClinicQueues}
                    variant="onBackground"
                  />
                </div>
              )}
              {(!availableClinicQueues || !availableClinicQueues.length) && (
                <StaffBodyTextS style={{ marginTop: "16px" }}>
                  {intl.formatMessage({ id: "conversation.to_queue.no_available_clinic_queue" })}
                </StaffBodyTextS>
              )}
            </div>
          )}

          {selectedClinic?.id === c.id && selectedQueue && (
            <div className={styles.sendButton}>
              <Button isDisabled={!selectedQueue} variant="primary" color="primary" onPress={sendToQueue}>
                {intl.formatMessage({ id: "label.send" })}
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default ToPhysicalQueue
